<template>
	<div
		class="w-full"
		id="journey-cases"
	>
		<GridLayout
			rightTitle="SAC"
			:reactiveSidebar="false"
			:sidebarState="filters.showSidebar"
		>
			<template #left-content>
				<Filters
					ref="filters"
					:showSidebar="filters.showSidebar"
					@apply="ApplyHandler"
					@hasAtLeastOneFilter="hasAtLeastOneFilter = $event"
				/>
			</template>
			<template #left-footer>
				<div
					class="w-8/12 h-full flex justify-center items-center gap-3"
				>
					<button
						class="btn btn-fixed-size"
						@click="ClearFiltersClick"
					>
						LIMPAR
					</button>
					<button
						class="btn btn-fixed-size"
						@click="SaveProfilesClick"
					>
						SALVAR
					</button>
					<button
						class="btn btn-fixed-size"
						@click="ApplyFiltersClick"
					>
						APLICAR
					</button>
				</div>
			</template>
			<template #right-tool-bar>
				<ToolBar>
					<div class="flex items-center py-1">
						<button
							class="btn btn-tool"
							:class="{
								'has-filters': hasAtLeastOneFilter,
							}"
							@click="HandleToggleSideBar"
						>
							<i
								class="fa-filter mx-2 font-20"
								:class="hasAtLeastOneFilter ? 'fas' : 'far'"
							></i>
						</button>
					</div>
					<div class="w-5/12 flex items-center py-1 gap-1">
						<SearchGroup
							:search="search"
							@change="search = $event"
							@search="GetCases()"
							@refresh="GetCases()"
						/>
					</div>
					<div class="flex-1 h-full flex justify-end">
						<div
							id="tab-list"
							class="h-full flex-1 justify-end"
						>
							<a
								class="tab-item"
								:class="{
									active: listType === 'UNRESOLVED',
								}"
								@click="listType = 'UNRESOLVED'"
								>NÃO RESOLVIDO
							</a>
							<a
								class="tab-item"
								:class="{
									active: listType === 'RESOLVED',
								}"
								@click="listType = 'RESOLVED'"
								>RESOLVIDO
							</a>
							<a
								class="tab-item"
								:class="{
									active: listType === 'EXPIRED',
								}"
								@click="listType = 'EXPIRED'"
								>EXPIRADO
							</a>
						</div>
					</div>
				</ToolBar>
			</template>
			<template #right-content>
				<NewDatatable
					v-model="cases"
					class="w-full"
					:headers="headers"
					emptyText="NÃO HÁ CASOS PARA EXIBIR"
					:rowColors="rowColorsMap"
					@dblclick="SelectCase"
				>
					<template #row="{ item }">
						<div class="datatable-cell">
							{{ item.scheduling.date }}
						</div>
						<div class="datatable-cell">
							{{ item.scheduling.time }}
						</div>
						<div class="datatable-cell">
							{{ item.patient.name.toUpperCase() }}
						</div>
						<div class="datatable-cell">
							{{ item.patient.phone }}
						</div>
						<div
							class="datatable-cell status"
							:class="[
								item.status.toLowerCase(),
								`${listType === 'EXPIRED' ? 'expired' : ''}`,
							]"
						>
							<b>
								{{ item.status_description.toUpperCase() }}
							</b>
						</div>
					</template>
				</NewDatatable>
			</template>
			<template #right-footer></template>
		</GridLayout>

		<JourneyCaseModal
			v-if="showJourneyCaseModal"
			:journeyId="selectedJourney.id"
			@close="CloseModalHandler"
			@refresh="GetCases(true)"
		/>
	</div>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex'
	import moment from 'moment'
	import {
		useCopyObject,
		useGetSelectedsRecursively,
		useError,
	} from '@/composables'

	import Filters from './components/Filters'
	import JourneyCaseModal from './components/JourneyCaseModal'

	export default {
		name: 'JourneyCases',
		components: {
			Filters,
			JourneyCaseModal,
		},
		data() {
			return {
				initializing: true,
				selecteds: [],
				cases: [],
				selectAll: false,
				headers: [
					{ text: 'DATA', value: 'scheduling.date', width: '7rem' },
					{ text: 'HORA', value: 'scheduling.time', width: '5rem' },
					{ text: 'NOME', value: 'patient.name' },
					{
						text: 'TELEFONE',
						value: 'patient.phone',
						width: '10rem',
					},
					{ text: 'STATUS', value: 'status', width: '11rem' },
				],
				listType: 'UNRESOLVED',
				selectedJourney: {
					id: null,
				},
				showJourneyCaseModal: false,
				search: '',
				limit: 200,
				offset: 0,
				profiles: [],
				saveFiltersDebounce: null,
				filters: {},
				hasAtLeastOneFilter: false,
				redirectCallback: null,
			}
		},
		computed: {
			...mapGetters('auth', [
				'requester_selected_id',
				'requester_selected_object',
				'account',
			]),
			rowColorsMap() {
				return {
					SENT: 'var(--sent)',
					RECEIVED: 'var(--received)',
					NO_ANSWERED: 'var(--no_answered)',
					NO_RECEIVED: 'var(--no_received)',
					SUSPENDED: 'var(--suspended)',
					CANCELLED: 'var(--cancelled)',
					ATTENDING: 'var(--attending)',
					PENDING: 'var(--pending)',
					NO_CALL_RESPONSE: 'var(--no_call_response)',
					RESOLVED: 'var(--resolved)',
					CONFIRMED: 'var(--confirmed)',
					EXPIRED: 'var(--expired)',
				}
			},
		},
		watch: {
			listType: {
				async handler(val) {
					await this.$refs.filters.ApplyFilters()
					await this.GetCases()
				},
			},
		},
		methods: {
			...mapActions('system', ['StartLoading', 'FinishLoading']),
			async GetCases(isRefresh = false) {
				if (!isRefresh) this.StartLoading()

				if (this.initializing && isRefresh) {
					return
				}

				const filters = await this.PrepareFiltersForSearch()

				return window.api
					.call('post', '/journey-cases/get-cases', {
						organization_id: this.requester_selected_id,
						type: this.listType,
						search: this.search,
						limit: this.limit,
						offset: this.offset,
						filters,
					})
					.then(({ status, data }) => {
						if (status !== 200) {
							return useError(
								data.message ??
									'Erro ao obter os lembretes de pacientes!'
							)
						}

						this.cases = data.cases.map(c => {
							c.scheduling.date = moment(
								c.scheduling.date_time
							).format('DD/MM/YYYY')
							c.scheduling.time = moment(
								c.scheduling.date_time
							).format('HH:mm')

							return c
						})
					})
					.finally(() => {
						if (!isRefresh) this.FinishLoading()
					})
			},
			async PrepareFiltersForSearch() {
				const filters = useCopyObject(this.filters)

				filters.statuses = filters.statuses
					.filter(s => s.checked)
					.map(s => s.value)

				const { schedules } = useGetSelectedsRecursively(
					this.filters.modules
				)
				filters.schedules = schedules

				return filters
			},
			ClearFiltersClick() {
				this.$refs.filters.ClearFilters()
			},
			SaveProfilesClick() {
				this.$refs.filters.SaveProfiles()
			},
			ApplyFiltersClick() {
				this.$refs.filters.ApplyFilters()
			},
			ApplyHandler({ filters }) {
				if (filters) this.filters = { ...filters }

				this.GetCases()
			},
			HandleToggleSideBar() {
				this.filters.showSidebar = !this.filters.showSidebar
			},
			HandleSearch(search) {
				this.search = search
			},
			async SelectCase(journeyCase) {
				if (
					journeyCase.author &&
					journeyCase.author.id !== this.account.id &&
					journeyCase.status === 'ATTENDING'
				) {
					return useError(
						`Em atendimento por <b>${journeyCase.author.name.toUpperCase()}</b>!`
					)
				}

				this.selectedJourney = journeyCase

				this.showJourneyCaseModal = true
			},
			CloseModalHandler() {
				this.showJourneyCaseModal = false

				this.GetCases()
			},
		},
	}
</script>

<style lang="scss" scoped>
	:deep(.status) {
		&.sent {
			color: var(--sent);
		}
		&.received {
			color: var(--received);
		}
		&.no_answered {
			color: var(--no_answered);
		}
		&.no_received {
			color: var(--no_received);
		}
		&.suspended {
			color: var(--suspended);
		}
		&.cancelled {
			color: var(--cancelled);
		}
		&.attending {
			color: var(--attending);
		}
		&.pending {
			color: var(--pending);
		}
		&.no_call_response {
			color: var(--no_call_response);
		}
		&.resolved {
			color: var(--resolved);
		}
		&.confirmed {
			color: var(--confirmed);
		}
	}
</style>
